import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFlashMessage } from "../../../components/FlashMessageProvider";

const asString = (value) => {
  if (value === null || value === undefined) return "";
  return value.toString();
};

export default function AdminSocialMediaUpdateDialog({
  open,
  onClose,
  onSave,
  initial = null,
  socialId = null,
  socialOptions = [],
  isSaving = false,
}) {
  const { showMessage } = useFlashMessage();
  const [form, setForm] = useState({ description: "", embedUrl: "", createdAt: "", socialMedia: asString(socialId) });

  const normalizedOptions = useMemo(() => {
    if (!Array.isArray(socialOptions)) return [];
    return socialOptions
      .map((option) => {
        if (!option) return null;
        const id = option._id || option.id;
        if (!id) return null;
        const labelParts = [option.platform || "Social Media"];
        if (option.handle) labelParts.push(`(${option.handle})`);
        return {
          id: id.toString(),
          label: labelParts.join(" "),
          source: option,
        };
      })
      .filter(Boolean);
  }, [socialOptions]);

  const findMatchingId = useCallback(
    (candidate) => {
      const str = asString(candidate).trim();
      if (!str) return "";
      const directMatch = normalizedOptions.find((option) => option.id === str);
      if (directMatch) return directMatch.id;
      const looseMatch = normalizedOptions.find((option) => {
        const { source } = option;
        if (!source) return false;
        if (source.handle && source.handle === str) return true;
        if (source.platform && source.platform === str) return true;
        return false;
      });
      return looseMatch ? looseMatch.id : str;
    },
    [normalizedOptions]
  );

  const resolveInitialSocial = useCallback(
    (value) => {
      if (!value) return "";
      if (typeof value === "object" && !(value instanceof Date)) {
        const nestedId = value._id || value.id;
        if (nestedId) return findMatchingId(nestedId);
        if (value.handle) {
          const viaHandle = findMatchingId(value.handle);
          if (viaHandle) return viaHandle;
        }
        if (value.platform) {
          const viaPlatform = findMatchingId(value.platform);
          if (viaPlatform) return viaPlatform;
        }
      }
      return findMatchingId(value);
    },
    [findMatchingId]
  );

  useEffect(() => {
    if (initial) {
      const resolvedSocial = (() => {
        if (initial.socialMedia) return resolveInitialSocial(initial.socialMedia);
        if (initial.socialMediaId || initial.socialMediaID) {
          return findMatchingId(initial.socialMediaId || initial.socialMediaID);
        }
        return "";
      })();
      setForm({
        description: initial.description || "",
        embedUrl: initial.embedUrl || initial.postUrl || initial.shareUrl || initial.url || "",
        createdAt: initial.createdAt ? (initial.createdAt instanceof Date ? initial.createdAt.toISOString().slice(0,16) : new Date(initial.createdAt).toISOString().slice(0,16)) : "",
        socialMedia: resolvedSocial || asString(socialId) || "",
      });
    } else {
      const defaultSocial = (() => {
        if (socialId) return findMatchingId(socialId);
        const first = normalizedOptions[0];
        return first ? first.id : "";
      })();
      setForm({ description: "", embedUrl: "", createdAt: "", socialMedia: defaultSocial });
    }
  }, [initial, open, socialId, normalizedOptions, resolveInitialSocial, findMatchingId]);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSaving) return;
    if (!form.socialMedia) {
      showMessage('Select a social media account before saving.', { type: 'error' });
      return;
    }
    const payload = {
      socialMediaId: form.socialMedia,
      description: form.description || '',
    };
    if (form.embedUrl && form.embedUrl.trim()) {
      payload.embedUrl = form.embedUrl.trim();
    }
    if (form.createdAt) {
      const iso = new Date(form.createdAt).toISOString();
      payload.createdAt = iso;
    }
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 p-4 sm:items-center">
      <div className="relative bg-white rounded shadow-lg w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 text-3xl font-bold leading-none"
          aria-label="Close dialog"
        >
          &times;
        </button>
        <h2 className="text-xl font-semibold mb-4">{initial ? "Edit Update" : "Add Update"}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Social Media Platform</label>
            <select
              name="socialMedia"
              value={form.socialMedia || ""}
              onChange={handleChange}
              className="w-full border rounded p-2"
              disabled={isSaving || !normalizedOptions.length}
            >
              <option value="">Select Social Media</option>
              {normalizedOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
            {!normalizedOptions.length ? (
              <p className="mt-1 text-sm text-red-600">Add a social media account before creating updates.</p>
            ) : null}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea name="description" value={form.description} onChange={handleChange} className="w-full border rounded p-2" />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Embed URL (optional)</label>
            <input
              type="url"
              name="embedUrl"
              value={form.embedUrl}
              onChange={handleChange}
              className="w-full border rounded p-2"
              placeholder="https://www.facebook.com/..."
              disabled={isSaving}
            />
          </div>

          {/* <div>
            <label className="block text-sm font-medium mb-1">Date & Time</label>
            <input type="datetime-local" name="createdAt" value={form.createdAt || ''} onChange={handleChange} className="w-full border rounded p-2" />
          </div> */}

          {/* <div>
            <label className="block text-sm font-medium mb-1">Image (optional)</label>
            <input type="file" accept="image/*" onChange={handleFile} />
          </div> */}

          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded" disabled={isSaving}>Cancel</button>
            <button type="submit" className={`px-4 py-2 rounded text-white ${isSaving ? "bg-blue-300 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
